import { useCallback, useEffect } from 'react';

import type { UUID } from '@aline/common';
import { isValidEmail } from '@aline/common';
import { Apollo } from 'src/gql/generated';
import { useDispatchNotification } from 'src/helpers';
import { useDebouncedCallback } from 'use-debounce';

import {
  beginCannedFieldValueWrite,
  clearCannedFieldValueWrite,
  finishCannedFieldValueWrite,
  getIsCurrentCannedFieldValueWrite,
} from './canned-field-write-state';

const WRITE_DEBOUNCE_MS = 1000;

/**
 * Debounced value writer shared by every input that edits a canned field.
 *
 * A keystroke opens a write so Finish can see the value is not saved yet, and
 * every exit path closes it again: the mutation settling, a cancel from the
 * pencil lock, or an invalid email the signer never corrects. Leaving one open
 * would block signing with "wait for changes to finish saving" forever.
 *
 * A failed write only restores the saved value when no newer keystroke has
 * arrived, so a slow retry can't overwrite what the signer is typing now.
 */
export function useCannedFieldValueWrite(args: {
  cannedFieldId: UUID;
  isEmail: boolean;
  savedValue: string | null;
  setValue: (value: string) => void;
  workflowId: UUID;
}): {
  cancelWrite: () => void;
  flushWrite: () => void;
  writeValue: (next: string) => void;
} {
  const { cannedFieldId, isEmail, savedValue, setValue, workflowId } = args;
  const [updateCannedField] = Apollo.useUpdateCannedFieldMutation();
  const dispatchFailed = useDispatchNotification({
    message: 'Failed to update field.',
    variant: 'error',
  });
  const debouncedUpdate = useDebouncedCallback(
    async ({ next, revision }: { next: string; revision: number }) => {
      try {
        await updateCannedField({
          variables: { input: { id: cannedFieldId, value: next } },
        });
      } catch {
        if (getIsCurrentCannedFieldValueWrite(workflowId, cannedFieldId, revision))
          setValue(savedValue ?? '');
        dispatchFailed();
      } finally {
        finishCannedFieldValueWrite(workflowId, cannedFieldId, revision);
      }
    },
    WRITE_DEBOUNCE_MS
  );
  const cancelWrite = useCallback(() => {
    debouncedUpdate.cancel();
    clearCannedFieldValueWrite(workflowId, cannedFieldId);
  }, [cannedFieldId, debouncedUpdate, workflowId]);
  const writeValue = useCallback(
    (next: string) => {
      setValue(next);
      if (isEmail && next !== '' && !isValidEmail(next)) {
        cancelWrite();
        return;
      }
      debouncedUpdate({
        next,
        revision: beginCannedFieldValueWrite(workflowId, cannedFieldId),
      });
    },
    [cancelWrite, cannedFieldId, debouncedUpdate, isEmail, setValue, workflowId]
  );

  useEffect(() => () => debouncedUpdate.flush(), [debouncedUpdate]);

  return { cancelWrite, flushWrite: debouncedUpdate.flush, writeValue };
}
